import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card } from '../../../components/ui/card';
import { 
  Calendar, Clock, CalendarDays, FileText, 
  AlertCircle, CheckCircle, Users, Timer, 
  Settings, TrendingUp 
} from 'lucide-react';
import { timeManagementAPI } from '../../../services/api';

const TimeManagementDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    pendingAbsences: 0,
    pendingAttendances: 0,
    absencesToday: 0,
    employeesOnLeave: [],
    quotaTypes: 0,
    absenceTypes: 0,
    attendanceTypes: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await timeManagementAPI.getDashboard();
      const data = response.data;
      setStats({
        pendingAbsences: data.total_pending_absence_requests || 0,
        pendingAttendances: data.total_pending_attendance_entries || 0,
        absencesToday: data.total_absences_today || 0,
        employeesOnLeave: data.employees_on_leave_today || [],
        quotaTypes: data.quota_types_count || 0,
        absenceTypes: data.absence_types_count || 0,
        attendanceTypes: data.attendance_types_count || 0
      });
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const modules = [
    {
      title: 'Absences',
      description: 'Manage employee absence requests and leave records',
      icon: CalendarDays,
      color: 'bg-red-500',
      path: '/hcm/time/absences',
      stat: stats.pendingAbsences,
      statLabel: 'Pending Requests',
      statColor: stats.pendingAbsences > 0 ? 'text-red-600' : 'text-gray-900'
    },
    {
      title: 'Attendances',
      description: 'Track overtime, business trips, and special attendances',
      icon: Clock,
      color: 'bg-orange-500',
      path: '/hcm/time/attendances',
      stat: stats.pendingAttendances,
      statLabel: 'Pending Entries',
      statColor: stats.pendingAttendances > 0 ? 'text-orange-600' : 'text-gray-900'
    },
    {
      title: 'Employee Quotas',
      description: 'View and manage employee leave quotas and balances',
      icon: TrendingUp,
      color: 'bg-green-500',
      path: '/hcm/time/employee-quotas',
      stat: stats.quotaTypes,
      statLabel: 'Quota Types'
    },
    {
      title: 'Configuration',
      description: 'Configure work schedules, holidays, and absence types',
      icon: Settings,
      color: 'bg-orange-500',
      path: '/hcm/time/config',
      stat: stats.absenceTypes + stats.attendanceTypes,
      statLabel: 'Types Configured'
    }
  ];

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="time-management-dashboard">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span>Time Management</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Time Management</h1>
          <p className="text-gray-600 mt-1">Manage employee time data, absences, and work schedules</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4" data-testid="stat-pending-absences">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending Absences</p>
                <p className={`text-xl font-semibold ${stats.pendingAbsences > 0 ? 'text-red-600' : ''}`}>
                  {loading ? '-' : stats.pendingAbsences}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4" data-testid="stat-on-leave">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Users className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">On Leave Today</p>
                <p className="text-xl font-semibold">{loading ? '-' : stats.absencesToday}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4" data-testid="stat-pending-attendances">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Timer className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending Attendances</p>
                <p className={`text-xl font-semibold ${stats.pendingAttendances > 0 ? 'text-orange-600' : ''}`}>
                  {loading ? '-' : stats.pendingAttendances}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4" data-testid="stat-quota-types">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Quota Types</p>
                <p className="text-xl font-semibold">{loading ? '-' : stats.quotaTypes}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Module Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {modules.map((module, idx) => (
            <Card 
              key={idx} 
              className="p-6 hover:shadow-lg transition-shadow cursor-pointer" 
              onClick={() => navigate(module.path)}
              data-testid={`time-module-${idx}`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${module.color}`}>
                  <module.icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{module.title}</h2>
                      <p className="text-sm text-gray-600 mt-1">{module.description}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${module.statColor || 'text-gray-900'}`}>
                        {loading ? '-' : module.stat}
                      </p>
                      <p className="text-xs text-gray-500">{module.statLabel}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Employees on Leave Today */}
        {stats.employeesOnLeave.length > 0 && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Employees on Leave Today</h3>
            <div className="space-y-3">
              {stats.employeesOnLeave.map((emp, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-sm font-medium">
                      {emp.employee_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{emp.employee_name}</p>
                      <p className="text-sm text-gray-500">{emp.employee_id}</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded-full">
                    {emp.absence_type}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Quick Actions */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button 
              onClick={() => navigate('/hcm/time/absences')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
              data-testid="action-create-absence"
            >
              <CalendarDays className="h-5 w-5 text-red-600" />
              <span className="text-sm font-medium">Record Absence</span>
            </button>
            <button 
              onClick={() => navigate('/hcm/time/attendances')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
              data-testid="action-create-attendance"
            >
              <Clock className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium">Record Attendance</span>
            </button>
            <button 
              onClick={() => navigate('/hcm/time/employee-quotas')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
              data-testid="action-view-quotas"
            >
              <TrendingUp className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium">View Quotas</span>
            </button>
            <button 
              onClick={() => navigate('/hcm/time/config')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
              data-testid="action-configure"
            >
              <Settings className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium">Configuration</span>
            </button>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default TimeManagementDashboard;
